export type BlockOp<T> = { kind: "eq" | "add" | "del"; item: T };

export type Segment<T> = { kind: "ops"; ops: BlockOp<T>[] } | { kind: "fold"; ops: BlockOp<T>[] };

/** Past this many cells the LCS table costs too much memory; callers fall back to the source diff. */
export const MAX_CELLS = 4_000_000;

/**
 * Longest-common-subsequence diff of two block lists compared by `key`. Within a changed run each
 * edited block reads old-then-new: when the removed and added blocks have the same kinds in the
 * same order (`kind`, e.g. paragraph then table) they are interleaved pairwise, otherwise all
 * removals come first. Returns null when the inputs are too large to compare.
 */
export function diffBlocks<T>(a: T[], b: T[], key: (t: T) => string, kind: (t: T) => string = () => ""): BlockOp<T>[] | null {
  const n = a.length;
  const m = b.length;
  if ((n + 1) * (m + 1) > MAX_CELLS) return null;
  const ka = a.map(key);
  const kb = b.map(key);
  const w = m + 1;
  const lcs = new Uint32Array((n + 1) * w);
  for (let i = n - 1; i >= 0; i--)
    for (let j = m - 1; j >= 0; j--) lcs[i * w + j] = ka[i] === kb[j] ? lcs[(i + 1) * w + j + 1] + 1 : Math.max(lcs[(i + 1) * w + j], lcs[i * w + j + 1]);

  const ops: BlockOp<T>[] = [];
  let dels: BlockOp<T>[] = [];
  let adds: BlockOp<T>[] = [];
  const flush = () => {
    if (dels.length > 1 && dels.length === adds.length && dels.every((d, k) => kind(d.item) === kind(adds[k].item))) dels.forEach((d, k) => ops.push(d, adds[k]));
    else ops.push(...dels, ...adds);
    dels = [];
    adds = [];
  };
  let i = 0;
  let j = 0;
  while (i < n || j < m) {
    if (i < n && j < m && ka[i] === kb[j]) {
      flush();
      ops.push({ kind: "eq", item: b[j] });
      i++;
      j++;
    } else if (j < m && (i === n || lcs[i * w + j + 1] >= lcs[(i + 1) * w + j])) adds.push({ kind: "add", item: b[j++] });
    else dels.push({ kind: "del", item: a[i++] });
  }
  flush();
  return ops;
}

/** Folds runs of unchanged blocks, keeping `context` blocks next to each change visible. */
export function foldUnchanged<T>(ops: BlockOp<T>[], context = 1): Segment<T>[] {
  const out: Segment<T>[] = [];
  let k = 0;
  while (k < ops.length) {
    if (ops[k].kind !== "eq") {
      const start = k;
      while (k < ops.length && ops[k].kind !== "eq") k++;
      out.push({ kind: "ops", ops: ops.slice(start, k) });
      continue;
    }
    const start = k;
    while (k < ops.length && ops[k].kind === "eq") k++;
    const run = ops.slice(start, k);
    const head = start === 0 ? 0 : context;
    const tail = k === ops.length ? 0 : context;
    if (run.length - head - tail < 2) {
      out.push({ kind: "ops", ops: run });
      continue;
    }
    if (head) out.push({ kind: "ops", ops: run.slice(0, head) });
    out.push({ kind: "fold", ops: run.slice(head, run.length - tail) });
    if (tail) out.push({ kind: "ops", ops: run.slice(run.length - tail) });
  }
  return out;
}
